import { useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { NativeSelect } from '@mantine/core';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export interface DateOfBirthInputProps {
  /** 'YYYY-MM-DD' (or a Date). null while empty. */
  value: string | Date | null | undefined;
  /** Called with 'YYYY-MM-DD' once day, month and year are all chosen; null otherwise. */
  onChange: (value: string | null) => void;
  /** Defaults to "Date of birth". */
  label?: ReactNode;
  /** Why we ask, in one line. */
  description?: ReactNode;
  /** Inline error under the three boxes. */
  error?: ReactNode;
  required?: boolean;
  /** Oldest year offered = this year minus maxAgeYears (default 110). */
  maxAgeYears?: number;
  /** Newest year offered = this year minus minAgeYears (default 0). */
  minAgeYears?: number;
  disabled?: boolean;
}

function toParts(value: string | Date | null | undefined): { d: string; m: string; y: string } {
  if (!value) return { d: '', m: '', y: '' };
  let y: number, m: number, d: number;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return { d: '', m: '', y: '' };
    y = value.getFullYear(); m = value.getMonth() + 1; d = value.getDate();
  } else {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (!match) return { d: '', m: '', y: '' };
    y = Number(match[1]); m = Number(match[2]); d = Number(match[3]);
  }
  return { d: String(d), m: String(m), y: String(y) };
}

function daysIn(month: number, year: number): number {
  if (!month) return 31;
  return new Date(year || 2000, month, 0).getDate();
}

/**
 * Date of birth as three dropdowns (day, month, year). Much easier than a calendar pop-up for
 * someone born decades ago, and phones show their own large wheel pickers for native selects.
 */
export function DateOfBirthInput({
  value, onChange, label = 'Date of birth', description, error, required, maxAgeYears = 110, minAgeYears = 0, disabled,
}: DateOfBirthInputProps) {
  const id = useId();
  const [parts, setParts] = useState(() => toParts(value));

  // Follow the parent when it sets a complete date (for example when the profile loads).
  const valueKey = value instanceof Date ? (Number.isNaN(value.getTime()) ? '' : value.toISOString().slice(0, 10)) : value ?? '';
  useEffect(() => {
    const next = toParts(value);
    if (next.y && next.m && next.d) setParts(next);
  }, [valueKey]);

  const thisYear = new Date().getFullYear();
  const years = useMemo(() => {
    const newest = thisYear - Math.max(0, minAgeYears);
    const oldest = thisYear - Math.max(minAgeYears + 1, maxAgeYears);
    const list: string[] = [];
    for (let y = newest; y >= oldest; y--) list.push(String(y));
    return list;
  }, [thisYear, minAgeYears, maxAgeYears]);

  const maxDay = daysIn(Number(parts.m), Number(parts.y));
  const days = useMemo(() => Array.from({ length: maxDay }, (_, i) => String(i + 1)), [maxDay]);

  const update = (patch: Partial<typeof parts>) => {
    const next = { ...parts, ...patch };
    const limit = daysIn(Number(next.m), Number(next.y));
    if (next.d && Number(next.d) > limit) next.d = String(limit);
    setParts(next);
    if (next.d && next.m && next.y) {
      onChange(`${next.y}-${next.m.padStart(2, '0')}-${next.d.padStart(2, '0')}`);
    } else {
      onChange(null);
    }
  };

  const invalid = !!error;
  return (
    <fieldset className="rides-fieldset" aria-describedby={[description ? `${id}-desc` : '', error ? `${id}-err` : ''].filter(Boolean).join(' ') || undefined}>
      <legend>
        {label}
        {required && <span className="rides-req" aria-hidden="true">*</span>}
      </legend>
      <div className="rides-dob">
        <NativeSelect
          aria-label="Day"
          value={parts.d}
          onChange={(e) => update({ d: e.currentTarget.value })}
          data={[{ value: '', label: 'Day' }, ...days.map((d) => ({ value: d, label: d }))]}
          error={invalid ? true : undefined}
          required={required}
          disabled={disabled}
          autoComplete="bday-day"
        />
        <NativeSelect
          aria-label="Month"
          value={parts.m}
          onChange={(e) => update({ m: e.currentTarget.value })}
          data={[{ value: '', label: 'Month' }, ...MONTHS.map((name, i) => ({ value: String(i + 1), label: name }))]}
          error={invalid ? true : undefined}
          required={required}
          disabled={disabled}
          autoComplete="bday-month"
        />
        <NativeSelect
          aria-label="Year"
          value={parts.y}
          onChange={(e) => update({ y: e.currentTarget.value })}
          data={[{ value: '', label: 'Year' }, ...years.map((y) => ({ value: y, label: y }))]}
          error={invalid ? true : undefined}
          required={required}
          disabled={disabled}
          autoComplete="bday-year"
        />
      </div>
      {description && <p className="desc" id={`${id}-desc`}>{description}</p>}
      {error && <p className="err" id={`${id}-err`} role="alert">{error}</p>}
    </fieldset>
  );
}
